// İSG uzmanı portalı — Ziyaretçiler (Visits) erişim sondası (route guard UX).
//
// Asıl yetki backend'dedir: her Ziyaretçi ucu `[ModuleAction("visits", …)]` taşır;
// grant'ı olmayan principal → 403 (api-contract §11). Bu sonda yalnız UX görünürlüğüdür.
//
// Sonda ucu olarak **güvenlik brifing** (`GET /visits/safety-briefing`, `visits/get`
// yetkili) seçilir — bilinçli bir KVKK kararı: brifing config **PII taşımaz**
// (yapılandırılmamışsa boş metin + version 0 döner), oysa "sahada" listesi ziyaretçi
// ad/telefon (PII) taşır. Menü her sayfada mount olduğundan, görünürlük sondası PII'siz
// olmalı ([[CLAUDE.md]] "gereksiz PII ekranda tutulmaz"). Sorgu anahtarı brifing config
// ekranıyla PAYLAŞILIR (`visitsKeys.safetyBriefing()`) — tek istek.
//   - 2xx  → erişim var (granted) — brifing verisi de döner (config ekranı yeniden kullanır)
//   - 403  → yetki yok (forbidden) → gizli menü + 403 sayfası
//   - diğer (404/503/ağ) → servis/veri kullanılamıyor (unavailable) → yeniden dene
//
// Ayarlar (§10) / Admin (§9) sondalarıyla aynı desendir; iş modülü olarak AYRIDIR.

import { ApiError, type SafetyBriefingDto, visitsApi } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { visitsKeys } from './visitsKeys';

export type VisitsAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface VisitsAccess {
  status: VisitsAccessStatus;
  /** Firmanın güvenlik brifingi (yalnız `granted` iken dolu; config ekranı yeniden kullanır). */
  briefing: SafetyBriefingDto | undefined;
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

/**
 * Ziyaretçiler erişimini backend sondasıyla belirler. Oturum açık (RequireAuth altı)
 * varsayılır. 4xx'te retry yok (anında sonuç); 403 (yetki yok) ≠ 404/503 (servis/veri)
 * ayrımı korunur.
 */
export function useVisitsAccess(): VisitsAccess {
  const query = useQuery({
    queryKey: visitsKeys.safetyBriefing(),
    queryFn: () => visitsApi.getSafetyBriefing(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: VisitsAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, briefing: query.data, refetch: () => void query.refetch() };
}
